import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Called when the component is connected to the DOM
   */
  connectedCallback() {
    super.connectedCallback();
    // Initialize button states when component is first loaded
    this.#updateButtonStates();
  }

  /**
   * Public sync hook for callers that set quantity programmatically.
   */
  syncButtonStates() {
    this.#checkQuantityRules();
    this.#updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * On Enter in quantity input: commit and run update (AJAX).
   * @param {KeyboardEvent} event
   */
  handleQuantityKeydown(event) {
    if (event.key !== 'Enter') return;
    if (!(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.#updateButtonStates();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const min = Number(quantityInput.min || 1);
    const max = quantityInput.max === '' ? Infinity : Number(quantityInput.max);
    const value = Number(quantityInput.value);
    const quantity = Number.isFinite(value) ? Math.floor(value) : min;
    quantityInput.value = String(Math.min(max, Math.max(min, quantity)));
  };

  /**
   * Updates the state of increase/decrease buttons based on current quantity
   */
  #updateButtonStates() {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value);
    const min = Number(quantityInput.min || 1);
    const max = Number(quantityInput.max) || Infinity;

    const minusButton = this.querySelector('.quantity-minus');
    const plusButton = this.querySelector('.quantity-plus');

    if (minusButton) {
      minusButton.disabled = currentValue <= min;
    }

    if (plusButton) {
      plusButton.disabled = currentValue >= max;
    }
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
